import { useEffect, useState } from 'react';
import './note-section.styles.scss';
import useAIActions from '../../../hooks/actions/ai.actions';
import useUserDetails from '../../../hooks/user-details.hook';
import cn from '../../../Utils/class-names.utils';
import GenerateButton from '../../Common/GenerateButton';
import { Spinner } from 'react-bootstrap';
import TrashCanFillIcon from '../../icons/trash-can-fill.icon,';
import PresetAnswers from '../../Common/preset-asnwers/preset-asnwers.component';
import AITextarea from '../../Common/ai-textarea';
import ImageLoader from '../../Common/ImageLoader';
import ImageWithInitials from '../../../Utils/ImageWithInitials';
import dateUtils from '../../../Utils/date.utils';


const NoteSection = ({ 
  onResize = () => {}, clientId, technologyEnum, refetchTools, submitNote, isLoading, value, userDetails, LastUpdatedBy,
  LastUpdated, placeholder = "Add note", isChild, disabled = false, className,
  buildPresetPrompt = () => {}, presets = [], 
  buildGeneratePrompt = () => {}, showGenerateButton = false,
  onTrashButtonClick = () => {},
  showTrashButton = false,
  counterUpdate = 0,
  isView = false
}) => {
  const { aiAssistant, isAssistantLoading } = useAIActions();
  const [isDataSet, setIsDataSet] = useState(false);
  const [inputTimeout, setInputTimeout] = useState(null);
  const [noteValue, setNoteValue] = useState('');
  const [sentValue, setSentValue] = useState('');
  const { language } = useUserDetails();

  const submiting = (value) => {
    if (isView) return;
    if (!submitNote) return;
    if (inputTimeout) clearTimeout(inputTimeout);
    if (sentValue === value) return;
    const timeout = setTimeout(() => {
      submitNote({ noteValue: value, technologyEnum, clientId })
        .finally(() => {
          refetchTools();
        });
      setSentValue(value);
      setInputTimeout(null);
    }, 800);
    setInputTimeout(timeout);
  }

  useEffect(() => {
    if (!value || isDataSet) return;
    setNoteValue(value);
    setSentValue(value);
    setIsDataSet(true);
  }, []);

  useEffect(() => {
    if (counterUpdate === 0) return;
    setNoteValue(value);
    setSentValue(value);
    setIsDataSet(true);
  }, [counterUpdate]);

  const onGenerateButtonClick = () => {
    if (isView) return;
    const createdPrompt = buildGeneratePrompt({ language });
    aiAssistant({ prompt: createdPrompt } )
      .then(({ message }) => {
        submitNote({ noteValue: message, technologyEnum, clientId })
          .finally(() => {
            refetchTools();
          });
        setNoteValue(message);
        setSentValue(message);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  return (
    <div className={cn("note-section oveflow-x-auto", className)} style={{
      padding: isChild ? '0px 48px' : '0px 32px'
    }}>
      {!isView && (
        <div className='mb-3 w-100'>
          {presets.length === 0 ? (
            <div className='d-flex align-items-center justify-content-between w-100'>
              <div className='no-wrap'>
                {showGenerateButton && (
                  <GenerateButton 
                    onButtonClick={onGenerateButtonClick} 
                    loading={isAssistantLoading}
                  />
                )}
              </div>
              {isLoading ? (
                <Spinner
                  animation="border"
                  variant="secondary"
                  size="sm"
                  style={{
                    width: "14px",
                    height: "14px",
                    marginRight: '8px'
                  }}
                />
              ) : (
                showTrashButton && (
                  <button className='trash-button my-button mr-1' onClick={onTrashButtonClick}>
                    <TrashCanFillIcon className="f-darkgrey" />
                  </button>
                )
              )}
            </div>
          ) : (
            presets.length > 0 && (
              <div className='w-100 d-flex align-items-center justify-content-between gap-8'>
                <PresetAnswers
                  presets={presets} 
                  onGenerateAnswer={(message) => {
                    submitNote({ noteValue: message, technologyEnum, clientId })
                      .finally(() => {
                        refetchTools();
                      });
                    setSentValue(message);
                    setNoteValue(message);
                  }}
                  buildPrompt={(prompt) => {
                    return buildPresetPrompt(prompt, language);
                  }}
                />
                {showTrashButton && (
                  <button className='trash-button my-button mr-1' onClick={onTrashButtonClick}>
                    <TrashCanFillIcon className="f-darkgrey" />
                  </button>
                )}
              </div>
            )
          )}
        </div>
      )}
      <div className='position-relative'>
        <AITextarea
          value={noteValue}
          placeholder={placeholder}
          onBlur={() => {
            if (isView) return;
            if (!submitNote) return;
            if (inputTimeout) clearTimeout(inputTimeout);
            if (sentValue === noteValue) return;
            submitNote({ noteValue: noteValue, technologyEnum, clientId })
              .finally(() => {
                refetchTools();
              });
            setSentValue(noteValue);
          }} 
          onChange={e => {
            if (isView) return;
            setNoteValue(e.target.value);
            onResize(e.target);
            submiting(e.target.value);
          }} 
          disabled={disabled || isView}
          onResize={onResize}
          onUpdate={({ message }) => {
            if (isView) return;
            submitNote({ noteValue: message, technologyEnum, clientId })
              .finally(() => {
                refetchTools();
              });
            setNoteValue(message);
            setSentValue(message);
          }}
          hideToolbar={isView}
        />
      </div>
      {LastUpdatedBy && userDetails && (
        <div className='last-updated d-flex align-items-md-center flex-column flex-md-row mt-1'>
          <span>Last updated by </span>
          <div className='d-flex align-items-center'>
            <div className='user-logo'>
              <ImageLoader
                src={userDetails.Avatar}
                alt=""
                className="ml-md-1 mr-1 rounded-full"
                width={16}
                height={16}
                noImageComponent={(
                  <div className="ml-md-1 mr-1 rounded-full">
                    <ImageWithInitials
                      initials={userDetails.Initials ?? userDetails?.FirstName?.charAt(0)}
                      width={16}
                      height={16}
                      background={"purple"}
                    />
                  </div>
                )}
              />
            </div>
            <div>
              {`${userDetails.FullName} at ${dateUtils.toDayMonthYearTimeFormat(LastUpdated)}`}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NoteSection;
